import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { Box, Stack, Inline, Text, xcss, Pressable } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import Spinner from '@atlaskit/spinner';
import CloseIcon from '@atlaskit/icon/core/close';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';

import { useLoadVideoDurations } from './hooks/useLoadVideoDurations';
import type { ContentFeatures } from './featuresExtractor';
import { calculateImageReadTime } from './readTimeUtils';

export const i18n = defineMessages({
	readTime: {
		id: 'read-time.read-time-popup',
		defaultMessage: 'Read time',
		description: 'Read time label',
	},
	loomVideos: {
		id: 'read-time.read-time-popup-loom-videos',
		defaultMessage: 'Loom videos',
		description: 'Loom videos label',
	},
	pageContent: {
		id: 'read-time.read-time-popup-page-content',
		defaultMessage: 'Page content',
		description: 'Page content label',
	},
	expands: {
		id: 'read-time.read-time-popup-expands',
		defaultMessage: 'Expands',
		description: 'Expands label',
	},
	expandTooltipDescription: {
		id: 'read-time.read-time-popup-expand-tooltip-description',
		defaultMessage: 'Loom videos and expands are excluded from read time estimates',
		description: 'Expand tooltip message',
	},
});

const popupStyles = xcss({
	width: '320px',
	position: 'relative',
	backgroundColor: 'elevation.surface.raised',
	border: `1px solid color.border`,
	borderRadius: 'border.radius.200',
	padding: 'space.300',
});

const headingContainerStyles = xcss({
	marginBottom: 'space.200',
});

const featureContainerStyles = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.100',
});

const pressableStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	borderRadius: '8px',
	paddingInline: 'space.100',
	height: '27px',
	display: 'flex',
	alignItems: 'center',

	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

const headingStyles = xcss({
	marginRight: 'space.100',
});

const infoIconStyles = xcss({
	paddingInlineStart: 'space.050',
	paddingBlockStart: 'space.025',
});

const formatLoomDuration = (ms: number): string => {
	const minutes = Math.floor(ms / 60000);
	const seconds = Math.round((ms % 60000) / 1000);

	if (minutes === 0) {
		return `${seconds} sec`;
	}
	if (seconds === 0) {
		return `${minutes} min`;
	}
	return `${minutes} min ${seconds} sec`;
};

const formatReadTimeInSeconds = (seconds: number): string => {
	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = Math.floor(seconds % 60);

	if (minutes === 0) {
		return `${remainingSeconds} sec`;
	}
	if (remainingSeconds === 0) {
		return `${minutes} min`;
	}
	return `${minutes} min ${remainingSeconds} sec`;
};

export const ReadTimePopup = ({
	features,
	featuresSkipExpand,
	onClose,
}: {
	features: ContentFeatures | null;
	featuresSkipExpand: ContentFeatures | null;
	onClose: () => void;
}) => {
	const { videoDurations, isLoadingDurations } = useLoadVideoDurations(features?.videos);

	if (!features && !featuresSkipExpand) {
		return null;
	}

	let expandsReadTime = 0;
	if (features && featuresSkipExpand) {
		if (features.wordCount !== featuresSkipExpand.wordCount) {
			expandsReadTime = ((features.wordCount - featuresSkipExpand.wordCount) / 265) * 60;
		}
	}
	const totalLoomDuration = Object.values(videoDurations).reduce<number>(
		(sum, duration) => sum + (duration as number),
		0,
	);
	const readTimeWithoutExpands = ((featuresSkipExpand?.wordCount || 0) / 265) * 60;
	const pageContentReadTime =
		readTimeWithoutExpands + calculateImageReadTime(features?.imageCount || 0);

	return (
		<Box testId="read-time-popup">
			<Stack xcss={popupStyles}>
				<Box key="readTime" xcss={[featureContainerStyles, headingContainerStyles]}>
					<Inline alignBlock="center" spread="space-between">
						<Inline alignBlock="center">
							<Box xcss={headingStyles}>
								<Heading size="medium">
									<FormattedMessage {...i18n.readTime} />
								</Heading>
							</Box>
							<Tooltip
								position="top"
								tag="span"
								content={<FormattedMessage {...i18n.expandTooltipDescription} />}
							>
								<Box xcss={infoIconStyles}>
									<InformationCircleIcon color={token('color.icon.subtlest')} label="Info" />
								</Box>
							</Tooltip>
						</Inline>
						<Pressable onClick={onClose} xcss={pressableStyles}>
							<CloseIcon label="Close" />
						</Pressable>
					</Inline>
				</Box>
				<Box key="wordCountSkipExpand" xcss={featureContainerStyles}>
					<Inline alignBlock="center" spread="space-between">
						<Text color="color.text.subtlest">
							<FormattedMessage {...i18n.pageContent} />
						</Text>
						<Text>{formatReadTimeInSeconds(pageContentReadTime)}</Text>
					</Inline>
				</Box>
				<Box key="loomDuration" xcss={featureContainerStyles}>
					<Inline alignBlock="center" spread="space-between">
						<Text color="color.text.subtlest">
							<FormattedMessage {...i18n.loomVideos} />
						</Text>
						{isLoadingDurations ? (
							<Spinner />
						) : (
							<Text>{formatLoomDuration(totalLoomDuration)}</Text>
						)}
					</Inline>
				</Box>
				<Box key="expandsReadTime" xcss={featureContainerStyles}>
					<Inline alignBlock="center" spread="space-between">
						<Text color="color.text.subtlest">
							<FormattedMessage {...i18n.expands} />
						</Text>
						<Text>{formatReadTimeInSeconds(expandsReadTime)} </Text>
					</Inline>
				</Box>
			</Stack>
		</Box>
	);
};
